import { configService } from './configService';

declare var Stripe: any;

export class DwspayService {
  private stripe: any = null;
  private elements: any = null;
  private paymentElement: any = null;
  private isPaymentElementValid: boolean = false;
  private paymentElementReadyCallbacks: Array<(ready: boolean) => void> = [];
  private paymentElementChangeCallbacks: Array<(event: any) => void> = [];

  /**
   * Initialize Stripe instance with platform account (not winery account)
   */
  public async initStripe(locale?: string): Promise<void> {
    if (this.stripe) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.injectIntoHead()
        .then(() => {
          let opts: any = {};

          // Set locale if supported
          const supportedLocales = [
            "ar", "da", "de", "en", "es", "fi", "fr", "he", "it", "ja",
            "hr", "nl", "no", "pl", "pt", "ru", "sv", "zh"
          ];

          if (locale && supportedLocales.includes(locale)) {
            opts.locale = locale;
          }

          // Use DWSPay publishable key (WS-Payments platform account)
          const dwspayPublishKey = configService.dwspayPublishKey;
          if (!dwspayPublishKey) {
            reject(new Error('DWSPay publishable key is not configured'));
            return;
          }

          this.stripe = Stripe(dwspayPublishKey, opts);
          // Don't create elements here - they need to be created with clientSecret in initPaymentElement
          resolve();
        })
        .catch(reject);
    });
  }

  /**
   * Initialize Payment Element with client secret and mount to container
   */
  public async initPaymentElement(
    clientSecret: string,
    elementContainerId: string = 'dwspay-payment-element',
    locale?: string
  ): Promise<void> {
    if (!this.stripe) {
      await this.initStripe(locale);
    }

    // Create Elements group with clientSecret (required for Payment Element)
    // If elements already exists, destroy it first to recreate with new clientSecret
    if (this.elements) {
      this.elements = null;
    }

    this.elements = this.stripe.elements({
      clientSecret: clientSecret
    });

    // Create Payment Element
    const appearance = {
      theme: 'stripe' as const,
    };

    this.paymentElement = this.elements.create('payment', {
      appearance: appearance
    });

    // Mount the Payment Element
    const container = document.getElementById(elementContainerId);
    if (!container) {
      throw new Error(`Container element with id "${elementContainerId}" not found`);
    }

    this.paymentElement.mount(`#${elementContainerId}`);

    // Listen for ready event
    this.paymentElement.on('ready', () => {
      this.paymentElementReadyCallbacks.forEach(callback => callback(true));
    });

    // Listen for change events (validation)
    this.paymentElement.on('change', (event: any) => {
      this.isPaymentElementValid = event.complete && !event.error;
      this.paymentElementChangeCallbacks.forEach(callback => callback(event));
    });
  }

  /**
   * Get Payment Element instance
   */
  public getPaymentElement(): any {
    return this.paymentElement;
  }

  /**
   * Submit Elements form - validates the form and collects payment details
   * MUST be called before confirmPayment() according to Stripe's deferred payment flow
   */
  public async submitElements(): Promise<any> {
    console.log('[DwspayService] submitElements called');

    if (!this.elements) {
      console.error('[DwspayService] Elements not initialized');
      throw new Error('Stripe Elements not initialized. Call initPaymentElement() first.');
    }

    if (!this.paymentElement) {
      console.error('[DwspayService] Payment Element not initialized');
      throw new Error('Payment Element not initialized. Call initPaymentElement() first.');
    }

    console.log('[DwspayService] Calling elements.submit() to validate form and collect payment details...');
    try {
      const submitResult = await this.elements.submit();

      if (submitResult.error) {
        console.error('[DwspayService] elements.submit() returned error:', submitResult.error);
        return { error: submitResult.error };
      } else {
        console.log('[DwspayService] elements.submit() succeeded - form validated and payment details collected');
        return { success: true };
      }
    } catch (error) {
      console.error('[DwspayService] Exception during elements.submit():', error);
      throw error;
    }
  }

  /**
   * Confirm payment with Payment Element
   * IMPORTANT: elements.submit() MUST be called before this method
   * See: https://stripe.com/docs/payments/accept-a-payment-deferred
   * @param clientSecret The payment intent client secret
   * @param returnUrlParams Optional parameters to include in return URL (e.g., reservation_id, is_reservation)
   */
  public async confirmPayment(clientSecret: string, returnUrlParams?: any): Promise<any> {
    console.log('[DwspayService] confirmPayment called with clientSecret:', (clientSecret && clientSecret.substring(0, 20)) + '...');

    if (!this.stripe) {
      console.log('[DwspayService] Stripe not initialized, initializing...');
      await this.initStripe();
    }

    if (!this.paymentElement) {
      console.error('[DwspayService] Payment Element not initialized');
      throw new Error('Payment Element not initialized. Call initPaymentElement() first.');
    }

    if (!this.elements) {
      console.error('[DwspayService] Elements not initialized');
      throw new Error('Stripe Elements not initialized. Call initPaymentElement() first.');
    }

    // Step 1: Submit elements form (validate and collect payment details)
    // This MUST be called before confirmPayment according to Stripe's deferred payment flow
    console.log('[DwspayService] Step 1: Submitting elements form...');
    const submitResult = await this.submitElements();

    if (submitResult.error) {
      console.error('[DwspayService] Elements submit failed, cannot proceed with confirmation');
      return { error: submitResult.error };
    }

    // Step 2: Confirm payment (must be called immediately after submit, no async operations in between)
    console.log('[DwspayService] Step 2: Calling stripe.confirmPayment...');
    // Construct return URL with existing query params and new ones
    let returnUrl = window.location.origin + window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    if (returnUrlParams) {
      Object.keys(returnUrlParams).forEach(key => {
        if (returnUrlParams[key] !== null && returnUrlParams[key] !== undefined) {
          searchParams.set(key, returnUrlParams[key]);
        }
      });
    }

    if (searchParams.toString()) {
      returnUrl += '?' + searchParams.toString();
    }

    console.log('[DwspayService] Step 2: Calling stripe.confirmPayment with return_url:', returnUrl);
    try {
      const result = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: clientSecret,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: 'if_required'
      });

      if (result.error) {
        console.error('[DwspayService] Stripe confirmPayment returned error:', result.error);
      } else {
        const status = (result.paymentIntent && result.paymentIntent.status) || 'unknown';
        console.log('[DwspayService] Stripe confirmPayment succeeded, payment status:', status);
        console.log('[DwspayService] PaymentIntent details:', {
          id: result.paymentIntent && result.paymentIntent.id,
          status: result.paymentIntent && result.paymentIntent.status,
          amount: result.paymentIntent && result.paymentIntent.amount
        });
      }

      return result;
    } catch (error) {
      console.error('[DwspayService] Exception during stripe.confirmPayment:', error);
      throw error;
    }
  }

  /**
   * Subscribe to Payment Element ready event
   */
  public onPaymentElementReady(callback: (ready: boolean) => void): void {
    this.paymentElementReadyCallbacks.push(callback);
  }

  /**
   * Subscribe to Payment Element change events (validation)
   */
  public onPaymentElementChange(callback: (event: any) => void): void {
    this.paymentElementChangeCallbacks.push(callback);
  }

  /**
   * Unsubscribe from Payment Element ready event
   */
  public offPaymentElementReady(callback: (ready: boolean) => void): void {
    this.paymentElementReadyCallbacks = this.paymentElementReadyCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Unsubscribe from Payment Element change events
   */
  public offPaymentElementChange(callback: (event: any) => void): void {
    this.paymentElementChangeCallbacks = this.paymentElementChangeCallbacks.filter(cb => cb !== callback);
  }

  /**
   * Check if Payment Element is valid and ready
   */
  public isPaymentElementValidState(): boolean {
    return this.isPaymentElementValid;
  }

  /**
   * Check if Payment Element is initialized
   */
  public isPaymentElementInitialized(): boolean {
    return this.paymentElement !== null;
  }

  /**
   * Wait for Payment Element to be ready
   */
  public async waitForPaymentElementReady(maxWaitMs: number = 5000): Promise<boolean> {
    if (this.paymentElement) {
      return true;
    }

    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.paymentElement) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (Date.now() - startTime >= maxWaitMs) {
          clearInterval(checkInterval);
          resolve(false);
        }
      }, 100);
    });
  }

  /**
   * Unmount and destroy Payment Element
   */
  public destroyPaymentElement(): void {
    if (this.paymentElement) {
      try {
        this.paymentElement.unmount();
        this.paymentElement = null;
      } catch (error) {
        console.error('Error destroying payment element:', error);
      }
    }
    this.isPaymentElementValid = false;
    this.elements = null;
  }

  /**
   * Check if DWSPay is enabled for a specific winery
   * @param wineryId The ID of the winery
   * @returns Promise with the dwspay status response
   */
  async checkDwspayStatus(wineryId: string): Promise<{ enabled: boolean; connected?: boolean; stripe_account_connect_id?: string }> {
    const url = `${configService.apiEndpoint}/wspayments/accounts/winery/${wineryId}/dwspay`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to check dwspay status: ${response.statusText}`);
    }
    return await response.json();
  }

  /**
   * Get payment methods available for a specific winery
   * @param wineryId The ID of the winery
   * @returns Promise with the payment methods response
   */
  async getPaymentMethods(wineryId: string): Promise<{ payment_methods?: string[], paymentMethods?: string[] }> {
    const url = `${configService.apiEndpoint}/wspayments/accounts/winery/${wineryId}/payment-methods`;
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to get payment methods: ${response.statusText}`);
    }
    return await response.json();
  }

  /**
   * Inject Stripe.js into head if not already loaded
   */
  private injectIntoHead(): Promise<any> {
    if ((window as any)['Stripe']) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error('Failed to load Stripe.js'));
      document.head.appendChild(script);
    });
  }
}

export const dwspayService = new DwspayService();
